import { Grid, Card, CardHeader, CardContent, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { Inventory2, CheckCircle, Lock, LocalShipping, Warning, HourglassBottom, TrendingDown } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { EmptyState } from '../../components/EmptyState';
import { inventoryApi } from '../../api/inventory.api';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const num = (n = 0) => Number(n).toLocaleString('en-IN');

export function InventoryDashboardPage() {
  const dash = useQuery({ queryKey: ['inventory', 'dashboard'], queryFn: () => inventoryApi.dashboard() });
  const reorder = useQuery({ queryKey: ['inventory', 'reorder'], queryFn: () => inventoryApi.reorder() });
  const whStock = useQuery({ queryKey: ['inventory', 'warehouse-stock'], queryFn: () => inventoryApi.warehouseStock() });
  const d = dash.data;

  return (
    <>
      <PageHeader title="Inventory Dashboard" subtitle="Stock value, availability, movement health and reorder signals." />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#1e40af" label="Total Stock Value" value={inr(d?.totalStockValue)} icon={<Inventory2 />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#16a34a" label="Available Stock" value={num(d?.availableStock)} icon={<CheckCircle />} hint={`Reserved ${num(d?.reservedStock)}`} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#7c3aed" label="Reserved Stock" value={num(d?.reservedStock)} icon={<Lock />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#0d9488" label="In Transit" value={num(d?.inTransitStock)} icon={<LocalShipping />} /></Grid>
      </Grid>
      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={4}><StatCard color="#d97706" label="Low Stock Items" value={num(d?.lowStock)} icon={<Warning />} /></Grid>
        <Grid item xs={12} sm={4}><StatCard color="#b45309" label="Slow Moving" value={num(d?.slowMoving)} icon={<HourglassBottom />} hint="No movement 90–180 days" /></Grid>
        <Grid item xs={12} sm={4}><StatCard color="#dc2626" label="Dead Stock" value={num(d?.deadStock)} icon={<TrendingDown />} hint="No movement > 180 days" /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Reorder Engine — items at/below reorder level" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(reorder.data?.length ?? 0) === 0 ? <EmptyState title="Nothing to reorder" dense /> : (
                <Table size="small">
                  <TableHead><TableRow><TableCell>Code</TableCell><TableCell>Item</TableCell><TableCell align="right">On hand</TableCell><TableCell align="right">Reorder</TableCell><TableCell align="right">Suggested</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(reorder.data ?? []).map((r) => (
                      <TableRow key={r.itemId} hover>
                        <TableCell>{r.code}</TableCell><TableCell>{r.name}</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>{r.onHand} {r.uom}</TableCell>
                        <TableCell align="right">{r.reorderLevel}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{r.suggestedQty}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Stock by Warehouse" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(whStock.data?.length ?? 0) === 0 ? <EmptyState title="No stock" dense /> : (
                <Table size="small">
                  <TableHead><TableRow><TableCell>Warehouse</TableCell><TableCell align="right">Items</TableCell><TableCell align="right">On hand</TableCell><TableCell align="right">Value</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(whStock.data ?? []).map((w) => (
                      <TableRow key={w.warehouseId} hover>
                        <TableCell>#{w.warehouseId}</TableCell><TableCell align="right">{w.itemCount}</TableCell>
                        <TableCell align="right">{num(w.onHand)}</TableCell><TableCell align="right">{inr(w.stockValue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
